import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { removeSonataSession, setSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';

import PromptModal from '@/components/common/PromptModal/PromptModal';
import { useAuth } from '@/context/AuthProvider';
import { useNavigationGuard } from '@/context/NavigationGuardProvider';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { useRedirectToLogin } from '@/hooks/redirectTo.hook';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getSession, removeSession } from '@/store/global/session/selectors';
import { getUser, reloadUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { getMasqueradeLogin } from '../../services/masquerade.service';
import { ChangeUserModal } from './ChangeUserModal/ChangeUserModal';
import { MainMenu } from './MainMenu/MainMenu';
import { MenuProvider, useMenu } from './Menu.context';
import { MenuActions } from './MenuActions';
import { MyProfile } from './MyProfile/MyProfile';
import { NotificationSettings } from './NotificationSettings';
import { UpdateBar } from './UpdateBar';
import { UserAlias } from './UserAlias';
import { UserMenu } from './UserMenu';

import './menu.styles.scss';

import type { MouseEvent } from 'react';
import type { Listeners } from './ChangeUserModal/ChangeUserModal';

function MenuContent() {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { refetch: authRefetch } = useAuth();
  const redirectToLogin = useRedirectToLogin();
  const { hasUnsavedChanges } = useNavigationGuard(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const currencyInfo = useGlobalStore(getBaseCurrency);
  const user = useGlobalStore(getUser).toJSON();
  const userRoles = useGlobalStore(getUserRoles);
  const sessionEntity = useGlobalStore(getSession);
  const session = sessionEntity ? sessionEntity.toJSON() : {};

  const runGuarded = (action: () => void) => {
    if (hasUnsavedChanges) {
      setPendingAction(() => action);
      return;
    }
    action();
  };

  const guardHardNavigation = (event: MouseEvent, action: () => void) => {
    if (!hasUnsavedChanges) {
      return;
    }
    event.preventDefault();
    setPendingAction(() => action);
  };

  const {
    showAliasModal,
    showNotificationSettingsModal,
    showMyProfileModal,
    state: {
      aliasModal: aliasModalShow,
      changeUserModal: changeUserModalShow,
      myProfileModal: myProfileModalShow,
      notificationSettingsModal: notificationsModalShow
    }
  } = useMenu();

  // Shared report sessions get a stripped-down menu: only the Sonata logo (no link,
  // no navigation, no user actions), so the viewer can't escape the locked report.
  if (isSharedReportSession(sessionEntity)) {
    return (
      <div className="menu">
        <div className="menu__content">
          <div className="menu__main">
            <MainMenu isShared user={user} userRoles={userRoles} />
          </div>
        </div>
      </div>
    );
  }

  if (isEmpty(user) || !userRoles || userRoles.length === 0) {
    return null;
  }

  const removeCurrentSession = async () => {
    removeSonataSession();
    removeSession();
    authRefetch?.().then(() => {
      redirectToLogin();
    });
  };

  const cleanCurrentSession = () => {
    removeSonataSession();
    removeSession();
  };

  const handleReload = () => {
    navigate('/campaigns', { replace: true });
    window.location.reload();
  };

  const handleMasqueradeUserChange = async (accessRequestId: string) => {
    try {
      const newSession = await getMasqueradeLogin(accessRequestId);
      cleanCurrentSession();
      setSonataSession(newSession);
      handleReload();
    } catch (_e) {
      removeCurrentSession();
    }
  };

  const reloadUserToken: Record<string, any> = {};
  const handleMyProfileSave = async () => {
    await reloadUser(reloadUserToken);
  };

  const handleLogout = () => {
    runGuarded(() => removeCurrentSession());
  };

  const listeners: Listeners = {
    onBackToUser: handleReload,
    onMasqueradeUserChange: handleMasqueradeUserChange,
    onViewAsChange: handleReload
  };

  return (
    <div className="menu">
      <div className="menu__content">
        <div className="menu__main">
          <MainMenu guardHardNavigation={guardHardNavigation} user={user} userRoles={userRoles} />
        </div>
        <div className="menu__updatebar">
          <UpdateBar user={user} />
        </div>
        <div className="menu__user">
          <UserMenu
            currencyInfo={currencyInfo}
            onLogout={handleLogout}
            session={session as any}
            user={user}
            userRoles={userRoles}
          />
        </div>
      </div>
      <MenuActions user={user} userRoles={userRoles} />
      {changeUserModalShow === 'show' && (
        <ChangeUserModal
          id={'changeUserModal'}
          {...listeners}
          session={session as any}
          user={user}
          userRoles={userRoles}
        />
      )}
      {notificationsModalShow === 'show' && (
        <NotificationSettings onClose={() => showNotificationSettingsModal('hide')} user={user} userRoles={userRoles} />
      )}
      {myProfileModalShow === 'show' && (
        <MyProfile isOpen onClose={() => showMyProfileModal('hide')} onSave={handleMyProfileSave} />
      )}
      {aliasModalShow === 'show' && <UserAlias onClose={() => showAliasModal('hide')} user={user} />}
      {pendingAction && (
        <PromptModal
          cancelCallback={() => setPendingAction(null)}
          confirmCallback={() => {
            pendingAction();
            setPendingAction(null);
          }}
          texts={{
            buttons: {
              cancel: formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              }),
              confirm: formatMessage({
                defaultMessage: 'Discard and exit',
                id: 'wizardLayout.navigationGuard.confirmButton'
              })
            },
            description: formatMessage({
              defaultMessage: 'You will lose all unsaved progress if you leave this page.',
              id: 'wizardLayout.navigationGuard.description'
            }),
            title: formatMessage({
              defaultMessage: 'Are you sure you want to leave?',
              id: 'wizardLayout.navigationGuard.title'
            })
          }}
        />
      )}
    </div>
  );
}

export function Menu() {
  return (
    <MenuProvider>
      <MenuContent />
    </MenuProvider>
  );
}
